import { motion } from 'framer-motion';
import { CheckSquare, Check } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';

export default function UserFormConfirmScene({ onNext }) {
  const cursorPath = [
    { x: 500, y: 200, delay: 1.0 },
    { x: 585, y: 370, delay: 1.4, click: true },
  ];

  return (
    <PortalShell
      activeItem="User"
      overlay={<AutoCursor targets={cursorPath} startDelay={1.2} />}
    >
      <div data-testid="scene-user-form-confirm" className="relative h-full w-full" style={{ height: '100%' }}>
        {/* Dimmed faux-review background (ghost) */}
        <div className="absolute inset-0 opacity-30 pointer-events-none px-8 pt-5">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <h3 className="font-heading font-bold text-base text-[#0A0A0A] mb-2">Review user details</h3>
            <p className="font-body text-xs text-[#737373] mb-3">Please review the details for this user, a link will…</p>
            <div className="space-y-3">
              <div><p className="text-[11px] font-body text-[#737373]">First name*</p><p className="text-sm font-body">Ahmed</p></div>
              <div><p className="text-[11px] font-body text-[#737373]">Business email*</p><p className="text-sm font-body">Asamoe@yourhealth.com</p></div>
              <div><p className="text-[11px] font-body text-[#737373]">User type</p><p className="text-sm font-body">Doctor</p></div>
            </div>
          </div>
        </div>

        {/* Dark dim overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 bg-[#0A0A0A] pointer-events-none"
        />

        {/* Modal (absolute, centered) */}
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-2xl border border-black/5 shadow-2xl p-7 text-center w-full max-w-md relative"
          >
            <div className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] cursor-pointer">
              <span className="text-[#737373] text-base font-bold">×</span>
            </div>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
              className="w-14 h-14 rounded-full bg-[#F5F5F5] flex items-center justify-center mx-auto mb-4"
            >
              <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center">
                <CheckSquare size={20} className="text-white" />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="font-body text-sm text-[#737373] leading-relaxed mb-5"
            >
              We&apos;ll send an email to this user to help set up the sign in.<br />Please confirm to connect.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] mb-5"
            >
              <p className="font-body font-medium text-sm text-[#0A0A0A]">asamoe@yourhealth.com</p>
            </motion.div>

            <motion.button
              data-testid="confirm-email-button"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: [1, 1, 1.08, 0.97, 1.05, 1],
                boxShadow: [
                  '0 10px 15px -3px rgba(13,148,136,0.25)',
                  '0 10px 15px -3px rgba(13,148,136,0.25)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 10px 15px -3px rgba(13,148,136,0.25)',
                  '0 0 0 8px rgba(13,148,136,0.25)',
                  '0 10px 15px -3px rgba(13,148,136,0.25)',
                ],
              }}
              transition={{ opacity: { delay: 1.1 }, scale: { duration: 2.0, delay: 2.8, times: [0, 0.2, 0.4, 0.6, 0.8, 1] } }}
              className="inline-flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm"
              onClick={onNext}
            >
              Confirm <Check size={14} />
            </motion.button>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="mt-4 flex items-center justify-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <p className="font-body text-[11px] text-[#737373]">
                Invite will be valid for <span className="font-medium text-[#0A0A0A]">3 days</span>
              </p>
              <InfoSpot
                text="The user has 3 days to accept the invite and set up their account. After that, the invite expires and the admin needs to resend it."
                position="top"
                color="amber"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </PortalShell>
  );
}
